import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ClipboardList, Download, Plus, Trash2, Users } from 'lucide-react'
import { DiktatFeld, Modal, Rueckfrage, VorschlagsKasten } from '@symphenia/ui-components'
import {
  MONTAGE_ABNAHME_ERGEBNISSE,
  MONTAGE_DETAILGRADE,
  MONTAGE_EXC,
  MONTAGE_RICHTUNGEN,
  MONTAGE_UMFAENGE,
  MONTAGE_VERFAHREN,
  MONTAGE_VERFAHREN_TEXT,
  MONTAGE_ZUSTAND_TEXT,
  erfuelltRecht,
  montageAbnahmeLeer,
  montageAbweichung,
  montageAktivePunkte,
  montageBauteilNeu,
  montageBauteilZuordnen,
  montageDetailgradVorschlag,
  montageDetailgradWechseln,
  montageExcAus,
  montageExtra,
  montageFotoPflicht,
  montageMangelAusDiktat,
  montageMessErgebnis,
  montageMitAbnahme,
  montageMitMontage,
  montageNaechsteNr,
  montagePunktErgebnis,
  montageSortiert,
  montageVerlustBeiWechsel,
  montageVermessGeltend,
  montageVorlageAnwenden,
  type MontageAbnahmeKandidat,
  type MontageBauteil,
  type MontageDetailgrad,
  type MontageDiktatMangel,
  type MontageInhalt,
  type MontageMangel,
  type MontagePunkt,
  type MontageUmfang,
  type MontageVorlage,
  type SiteDocAiProposal,
  type SiteDocAiProposalItem,
  type SiteDocDetail,
} from '@symphenia/shared-types'
import { useAuthStore } from '@/lib/auth.store'
import { fehlertext } from '@/lib/fehlertext'
import { FeldStatus, feldKlasse, useAenderung, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { cn } from '@/lib/utils'
import {
  fetchAssemblyCandidates,
  fetchAssemblyPrefill,
  fetchAssemblyProjectParts,
  fetchAssemblySettings,
  linkAssemblyPart,
  reopenAssemblyDefect,
  resolveAssemblyDefect,
  siteDocAiProposal,
  updateSiteDoc,
  type MontagePosition,
} from '../../api/site-docs.api'
import type { ErpDetailProps } from '../registry'
import { datumDe } from './MontageErpVorzeige'

/** Der Inhalt in fester Form — ältere oder halbe Entwürfe bekommen die fehlenden Teile. */
export function montageInhaltVon(roh: Record<string, unknown> | null | undefined): MontageInhalt {
  const r = (roh ?? {}) as Partial<MontageInhalt>
  const umfang = r.umfang ?? 'MONTAGE_ABNAHME'
  return {
    umfang,
    zeitraum: r.zeitraum ?? null,
    ausfuehrende: Array.isArray(r.ausfuehrende) ? r.ausfuehrende : [],
    bauteile: Array.isArray(r.bauteile) ? r.bauteile : [],
    maengel: Array.isArray(r.maengel) ? r.maengel : [],
    abnahme: montageMitAbnahme(umfang) ? r.abnahme ?? montageAbnahmeLeer() : null,
    bemerkung: r.bemerkung ?? null,
  }
}

const schluessel = () => crypto.randomUUID().replace(/-/g, '').slice(0, 16)
const naechste = (b: readonly MontageBauteil[]) => (b.length ? Math.max(...b.map((x) => x.sortOrder)) + 1 : 0)
const kopf = 'px-2 py-1 text-left text-xs font-medium text-muted-foreground'
const zelle = 'px-2 py-1 align-top'
const knopf = 'inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs'

/** Ein Text, der beim Verlassen speichert — über die Operation der Speicherstelle, nie mit eigenem PATCH. */
function TextFeld({ wert, onSpeichern, gesperrt, platzhalter, mehrzeilig, className, ...rest }: { wert: string | null; onSpeichern: (w: string | null) => void; gesperrt?: boolean; platzhalter?: string; mehrzeilig?: boolean; className?: string; 'aria-label'?: string; inputMode?: 'decimal' | 'numeric' }) {
  const [lokal, setLokal] = useState(wert ?? '')
  useEffect(() => setLokal(wert ?? ''), [wert])
  const props = {
    className: cn(feldKlasse, className),
    disabled: gesperrt,
    placeholder: platzhalter,
    value: lokal,
    'aria-label': rest['aria-label'] ?? platzhalter,
    onChange: (e: { target: { value: string } }) => setLokal(e.target.value),
    onBlur: () => (lokal || null) !== (wert || null) && onSpeichern(lokal.trim() ? lokal : null),
    onKeyDown: (e: { key: string }) => e.key === 'Escape' && setLokal(wert ?? ''),
  }
  return mehrzeilig ? <textarea {...props} className={cn(props.className, 'min-h-16')} /> : <input {...props} inputMode={rest.inputMode} />
}

/**
 * AP-47 11.2 — **Detailteil Montageprotokoll im ERP.** Im Entwurf derselbe Inhalt wie in der App, geschrieben
 * als Ganzes über eine Speicherstelle mit Operation (AP-36 8.3 a). In jedem eingefrorenen Zustand: Zuordnung je
 * Bauteil zu Position und Arbeitsschritt (`sitedocs.assembly.manage`, mit Projektverwaltung) und die Mängel mit
 * Bürostand — „als behoben vermerken", „Vermerk zurücknehmen", Aufgabe öffnen.
 */
export function MontageErpDetail({ dok, onGeaendert }: ErpDetailProps) {
  const entwurf = dok.status === 'DRAFT' && !dok.heldByUserId && dok.can.edit
  return entwurf ? <MontageEditor dok={dok} onGeaendert={onGeaendert} /> : <MontageBuero dok={dok} onGeaendert={onGeaendert} />
}

// ── Entwurf ─────────────────────────────────────────────────────────────────

function MontageEditor({ dok, onGeaendert }: ErpDetailProps) {
  const { t } = useTranslation()
  const { hasModule } = useAuthStore()
  const daten = useMemo<Datensatz>(() => ({ inhalt: dok.content ?? null, updatedAt: dok.updatedAt }), [dok.content, dok.updatedAt])
  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `montageInhalt:${dok.id}`,
    bezeichnung: `${t('siteDocs.kinds.ASSEMBLY')} ${dok.number ?? ''}`.trim(),
    daten,
    schreibe: async (patch) => {
      const neu = await updateSiteDoc('ASSEMBLY', dok.id, { content: patch.inhalt })
      onGeaendert(neu)
      return { inhalt: neu.content ?? null, updatedAt: neu.updatedAt }
    },
  }) as Speicherstelle<unknown>
  const { sende, status } = useAenderung(stelle, `montage:${dok.id}`)
  const inhalt = montageInhaltVon(dok.content)
  const aendere = useCallback(
    (f: (i: MontageInhalt) => MontageInhalt) => void sende({ felder: ['inhalt'], labels: { inhalt: t('siteDocs.assembly.content') }, operation: (a) => ({ ...a, inhalt: f(montageInhaltVon(a.inhalt as Record<string, unknown> | null)) as unknown as Datensatz }) }),
    [sende, t],
  )
  const [vorlagen, setVorlagen] = useState<MontageVorlage[]>([])
  const [vorgabe, setVorgabe] = useState<MontageDetailgrad>('EINFACH')
  const [fehler, setFehler] = useState<string | null>(null)
  const [positionen, setPositionen] = useState<MontagePosition[] | null>(null)
  const [kandidaten, setKandidaten] = useState(false)

  useEffect(() => {
    fetchAssemblySettings()
      .then((e) => {
        const x = montageExtra(e.extra)
        setVorlagen(x.vorlagen)
        setVorgabe(x.detailgradVorgabe)
      })
      .catch(() => setVorlagen([]))
  }, [])

  const mitMontage = montageMitMontage(inhalt.umfang)
  const umfangGesperrt = !!dok.number
  const setzeBauteil = (b: MontageBauteil) => aendere((i) => ({ ...i, bauteile: i.bauteile.map((x) => (x.key === b.key ? b : x)) }))

  async function ausBuchungen() {
    setFehler(null)
    try {
      const namen = await fetchAssemblyPrefill({ projectId: dok.projectId, projectRefId: dok.projectRefId, date: dok.documentDate.slice(0, 10) })
      if (!namen.length) setFehler(t('siteDocs.assembly.noBookings'))
      aendere((i) => ({ ...i, ausfuehrende: [...new Set([...i.ausfuehrende, ...namen])] }))
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  async function ausAuftrag() {
    if (!dok.projectId) return
    setFehler(null)
    try {
      setPositionen(await fetchAssemblyProjectParts(dok.projectId))
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">{t('siteDocs.assembly.editHint')}</span>
        <FeldStatus status={status} />
      </div>
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
      {dok.can.dictate && <MontageErpDiktat dok={dok} inhalt={inhalt} aendere={aendere} />}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-muted-foreground">{t('siteDocs.assembly.scope')}</span>
        {MONTAGE_UMFAENGE.map((u: MontageUmfang) => (
          <button
            key={u}
            type="button"
            disabled={umfangGesperrt}
            className={cn(knopf, inhalt.umfang === u && 'border-primary bg-primary/10', umfangGesperrt && inhalt.umfang !== u && 'opacity-40')}
            onClick={() => aendere((i) => ({ ...i, umfang: u, abnahme: montageMitAbnahme(u) ? i.abnahme ?? montageAbnahmeLeer() : null }))}
          >
            {t(`siteDocs.assembly.scopes.${u}`)}
          </button>
        ))}
        {umfangGesperrt && <span className="text-xs text-muted-foreground">{t('siteDocs.assembly.scopeLockedHint')}</span>}
      </div>

      {mitMontage && (
        <div className="grid gap-2 sm:grid-cols-[auto_auto_1fr]">
          <label className="text-xs text-muted-foreground">
            {t('siteDocs.assembly.periodFrom')}
            <input type="date" className={feldKlasse} value={inhalt.zeitraum?.von ?? ''} onChange={(e) => aendere((i) => ({ ...i, zeitraum: e.target.value ? { von: e.target.value, bis: i.zeitraum?.bis && i.zeitraum.bis >= e.target.value ? i.zeitraum.bis : e.target.value } : null }))} />
          </label>
          <label className="text-xs text-muted-foreground">
            {t('siteDocs.assembly.periodTo')}
            <input type="date" className={feldKlasse} disabled={!inhalt.zeitraum} min={inhalt.zeitraum?.von} value={inhalt.zeitraum?.bis ?? ''} onChange={(e) => aendere((i) => (i.zeitraum ? { ...i, zeitraum: { von: i.zeitraum.von, bis: e.target.value || i.zeitraum.von } } : i))} />
          </label>
          <label className="text-xs text-muted-foreground">
            {t('siteDocs.assembly.performedBy')}
            <span className="flex gap-1">
              <TextFeld wert={inhalt.ausfuehrende.join(', ')} platzhalter={t('siteDocs.assembly.performedByHint')} onSpeichern={(w) => aendere((i) => ({ ...i, ausfuehrende: (w ?? '').split(',').map((n) => n.trim()).filter(Boolean) }))} />
              {(dok.projectId || dok.projectRefId) && (
                <button type="button" className={knopf} onClick={() => void ausBuchungen()} title={t('siteDocs.assembly.fromBookings')}>
                  <Users className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          </label>
        </div>
      )}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-medium">{t('siteDocs.assembly.parts')}</h3>
          <div className="flex flex-wrap gap-2">
            <button type="button" className={knopf} onClick={() => aendere((i) => ({ ...i, bauteile: [...i.bauteile, montageBauteilNeu(schluessel(), naechste(i.bauteile), '', null, vorgabe)] }))}>
              <Plus className="h-3.5 w-3.5" /> {t('siteDocs.assembly.addPart')}
            </button>
            {dok.projectId && hasModule('PROJECT_MGMT.CORE') && (
              <button type="button" className={knopf} onClick={() => void ausAuftrag()}>
                <ClipboardList className="h-3.5 w-3.5" /> {t('siteDocs.assembly.fromOrder')}
              </button>
            )}
            {montageMitAbnahme(inhalt.umfang) && (
              <button type="button" className={knopf} onClick={() => setKandidaten(true)}>
                <Download className="h-3.5 w-3.5" /> {t('siteDocs.assembly.fromRecords')}
              </button>
            )}
          </div>
        </div>
        {montageSortiert(inhalt.bauteile).map((b) => (
          <BauteilEditor
            key={b.key}
            bauteil={b}
            vorlagen={vorlagen}
            nurAbnahme={!mitMontage}
            onChange={setzeBauteil}
            onEntfernen={() => aendere((i) => ({ ...i, bauteile: i.bauteile.filter((x) => x.key !== b.key), maengel: i.maengel.map((m) => (m.bauteilKey === b.key ? { ...m, bauteilKey: null } : m)) }))}
            onMangel={(text) => aendere((i) => ({ ...i, maengel: [...i.maengel, { nr: montageNaechsteNr(i.maengel), bauteilKey: b.key, text, gewicht: null, frist: null, fotos: [] }] }))}
          />
        ))}
      </section>

      <MaengelEditor inhalt={inhalt} aendere={aendere} />
      {montageMitAbnahme(inhalt.umfang) && inhalt.abnahme && <AbnahmeEditor inhalt={inhalt} aendere={aendere} />}

      <label className="block text-xs text-muted-foreground">
        {t('siteDocs.assembly.remark')}
        <TextFeld mehrzeilig wert={inhalt.bemerkung} onSpeichern={(w) => aendere((i) => ({ ...i, bemerkung: w }))} />
      </label>

      {positionen && (
        <Modal isOpen onClose={() => setPositionen(null)} title={t('siteDocs.assembly.fromOrder')} size="md">
          {!positionen.length && <p className="text-muted-foreground">{t('siteDocs.assembly.noPositions')}</p>}
          <ul className="space-y-1">
            {positionen.map((p) => {
              const schon = inhalt.bauteile.some((b) => b.positionId === p.id)
              const exc = montageExcAus(p.executionClass)
              return (
                <li key={p.id}>
                  <button
                    type="button"
                    disabled={schon}
                    className="flex w-full flex-col items-start rounded-md border px-3 py-1.5 text-left disabled:opacity-50"
                    onClick={() => {
                      const schritt = p.steps.find((s) => s.isAssembly && s.status !== 'DONE' && s.status !== 'CANCELLED')?.id ?? null
                      aendere((i) => ({
                        ...i,
                        bauteile: [...i.bauteile, { ...montageBauteilNeu(schluessel(), naechste(i.bauteile), `${p.number} ${p.name}`, exc, vorgabe), detailgrad: montageDetailgradVorschlag(exc, vorgabe), positionId: p.id, workStepId: schritt }],
                      }))
                      setPositionen(null)
                    }}
                  >
                    <span className="font-medium">
                      {p.number} {p.name}
                    </span>
                    <span className="text-xs text-muted-foreground">{[p.executionClass, schon ? t('siteDocs.assembly.alreadyIn') : null].filter(Boolean).join(' · ')}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        </Modal>
      )}
      {kandidaten && <KandidatenDialog dok={dok} onClose={() => setKandidaten(false)} aendere={aendere} />}
    </div>
  )
}

function BauteilEditor({ bauteil: b, vorlagen, nurAbnahme, onChange, onEntfernen, onMangel }: { bauteil: MontageBauteil; vorlagen: MontageVorlage[]; nurAbnahme: boolean; onChange: (b: MontageBauteil) => void; onEntfernen: () => void; onMangel: (text: string) => void }) {
  const { t } = useTranslation()
  const [wechsel, setWechsel] = useState<MontageDetailgrad | null>(null)
  const punkt = (key: string, teil: Partial<MontagePunkt>) => onChange({ ...b, punkte: b.punkte.map((p) => (p.key === key ? { ...p, ...teil } : p)) })
  return (
    <div className="space-y-2 rounded-md border p-2">
      <div className="grid gap-2 sm:grid-cols-[2fr_1fr_auto_auto]">
        <TextFeld wert={b.bezeichnung} platzhalter={t('siteDocs.assembly.partName')} onSpeichern={(w) => onChange({ ...b, bezeichnung: w ?? '' })} />
        <TextFeld wert={b.menge} platzhalter={t('siteDocs.assembly.quantity')} onSpeichern={(w) => onChange({ ...b, menge: w })} />
        <select className={feldKlasse} aria-label={t('siteDocs.assembly.exc')} value={b.exc ?? ''} onChange={(e) => onChange({ ...b, exc: (e.target.value || null) as MontageBauteil['exc'] })}>
          <option value="">{t('siteDocs.assembly.excNone')}</option>
          {MONTAGE_EXC.map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </select>
        <button type="button" className={cn(knopf, 'text-red-700')} onClick={onEntfernen} title={t('siteDocs.assembly.removePart')}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      {!nurAbnahme && (
        <div className="flex flex-wrap items-center gap-2">
          <select className={cn(feldKlasse, 'w-auto')} aria-label={t('siteDocs.assembly.level')} value={b.detailgrad} onChange={(e) => {
            const neu = e.target.value as MontageDetailgrad
            if (montageVerlustBeiWechsel(b, neu).length) setWechsel(neu)
            else onChange(montageDetailgradWechseln(b, neu))
          }}>
            {MONTAGE_DETAILGRADE.map((d) => (
              <option key={d} value={d}>
                {t(`siteDocs.assembly.levels.${d}`)}
              </option>
            ))}
          </select>
          <select className={cn(feldKlasse, 'w-auto')} aria-label={t('siteDocs.assembly.template')} value={b.vorlage?.id ?? ''} onChange={(e) => onChange(montageVorlageAnwenden(b, vorlagen.find((v) => v.id === e.target.value) ?? null))}>
            <option value="">{b.vorlage && !vorlagen.some((v) => v.id === b.vorlage?.id) ? `${b.vorlage.name} (${t('siteDocs.assembly.version')} ${b.vorlage.fassung})` : t('siteDocs.assembly.noTemplate')}</option>
            {vorlagen.filter((v) => v.aktiv || v.id === b.vorlage?.id).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} ({t('siteDocs.assembly.version')} {v.fassung})
              </option>
            ))}
          </select>
        </div>
      )}
      {!nurAbnahme && montageAktivePunkte(b).length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b">
              <tr>
                <th className={kopf}>{t('siteDocs.assembly.point')}</th>
                <th className={kopf}>{t('siteDocs.assembly.valueEntry')}</th>
                <th className={kopf}>{t('siteDocs.assembly.result')}</th>
                <th className={kopf}>{t('siteDocs.assembly.remark')}</th>
              </tr>
            </thead>
            <tbody>
              {montageAktivePunkte(b).map((p) => {
                const erg = montagePunktErgebnis(p)
                const gerechnet = p.art === 'MESSEN' ? montageMessErgebnis(p) : null
                return (
                  <tr key={p.key} className="border-b last:border-0">
                    <td className={zelle}>
                      <span className="block text-xs text-muted-foreground">{p.gruppe}</span>
                      {p.text}
                      {montageFotoPflicht(p, b.detailgrad) && <span className="ml-1 text-xs text-amber-700">({t('siteDocs.assembly.photoRequired')})</span>}
                    </td>
                    <td className={zelle}>
                      {p.art === 'TEXT' ? (
                        <TextFeld wert={p.angabe} platzhalter={p.pflicht ? t('siteDocs.assembly.entryRequired') : t('siteDocs.assembly.entry')} onSpeichern={(w) => punkt(p.key, { angabe: w })} />
                      ) : p.art === 'MESSEN' ? (
                        <div className="flex gap-1">
                          <TextFeld className="w-20" inputMode="decimal" wert={p.messwert} platzhalter={`${t('siteDocs.assembly.value')} ${p.einheit ?? ''}`} onSpeichern={(w) => punkt(p.key, { messwert: w, ...(p.ueberschrieben ? {} : { ergebnis: p.ergebnis === 'NA' ? 'NA' : null }) })} />
                          <TextFeld className="w-16" inputMode="decimal" wert={p.soll} platzhalter={t('siteDocs.assembly.target')} onSpeichern={(w) => punkt(p.key, { soll: w })} />
                          <TextFeld className="w-14" inputMode="decimal" wert={p.tolMinus} platzhalter="−" onSpeichern={(w) => punkt(p.key, { tolMinus: w })} />
                          <TextFeld className="w-14" inputMode="decimal" wert={p.tolPlus} platzhalter="+" onSpeichern={(w) => punkt(p.key, { tolPlus: w })} />
                        </div>
                      ) : null}
                    </td>
                    <td className={zelle}>
                      {p.art !== 'TEXT' && (
                        <div className="flex gap-1">
                          {(['OK', 'NOK', 'NA'] as const).map((e) => (
                            <button
                              key={e}
                              type="button"
                              className={cn(knopf, erg === e && (e === 'OK' ? 'border-green-600 bg-green-600 text-white' : e === 'NOK' ? 'border-red-600 bg-red-600 text-white' : 'border-slate-500 bg-slate-500 text-white'))}
                              onClick={() => {
                                if (p.art === 'MESSEN') punkt(p.key, e === 'NA' ? { ergebnis: 'NA', ueberschrieben: false } : { ergebnis: e, ueberschrieben: !!gerechnet && gerechnet !== e })
                                else punkt(p.key, { ergebnis: e })
                                if (e === 'NOK') onMangel(p.text)
                              }}
                            >
                              {t(`siteDocs.assembly.results.${e}`)}
                            </button>
                          ))}
                        </div>
                      )}
                      {p.ueberschrieben && <span className="text-xs text-amber-700">{t('siteDocs.assembly.overridden')}</span>}
                    </td>
                    <td className={zelle}>
                      <TextFeld wert={p.bemerkung} platzhalter={t('siteDocs.assembly.remark')} onSpeichern={(w) => punkt(p.key, { bemerkung: w })} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      {!nurAbnahme && b.detailgrad === 'NACHWEIS' && b.tabellen.anziehprotokoll !== 'AUS' && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">
              {t('siteDocs.assembly.torque')}
              {b.tabellen.anziehprotokoll === 'PFLICHT' && ` (${t('siteDocs.assembly.required')})`}
            </h4>
            <button type="button" className={knopf} onClick={() => onChange({ ...b, anziehprotokoll: [...b.anziehprotokoll, { key: schluessel(), verbindung: '', garnitur: '', anzahl: null, verfahren: 'DREHMOMENT', sollMoment: null, pruefmittel: null, kalibriertBis: null, geprueft: null, ergebnis: null, bemerkung: null }] })}>
              <Plus className="h-3.5 w-3.5" /> {t('siteDocs.assembly.addRow')}
            </button>
          </div>
          {b.anziehprotokoll.map((z) => {
            const zeile = (teil: Partial<typeof z>) => onChange({ ...b, anziehprotokoll: b.anziehprotokoll.map((x) => (x.key === z.key ? { ...x, ...teil } : x)) })
            return (
              <div key={z.key} className="grid gap-1 sm:grid-cols-[2fr_2fr_4rem_9rem_5rem_6rem_8rem_4rem_auto_auto]">
                <TextFeld wert={z.verbindung} platzhalter={t('siteDocs.assembly.connection')} onSpeichern={(w) => zeile({ verbindung: w ?? '' })} />
                <TextFeld wert={z.garnitur} platzhalter={t('siteDocs.assembly.boltSet')} onSpeichern={(w) => zeile({ garnitur: w ?? '' })} />
                <TextFeld inputMode="numeric" wert={z.anzahl == null ? null : String(z.anzahl)} platzhalter={t('siteDocs.assembly.count')} onSpeichern={(w) => zeile({ anzahl: w ? Math.max(0, Math.trunc(Number(w)) || 0) : null })} />
                <select className={feldKlasse} aria-label={t('siteDocs.assembly.method')} value={(MONTAGE_VERFAHREN as readonly string[]).includes(z.verfahren) ? z.verfahren : ''} onChange={(e) => zeile({ verfahren: e.target.value || z.verfahren })}>
                  <option value="">{(MONTAGE_VERFAHREN as readonly string[]).includes(z.verfahren) ? t('siteDocs.assembly.otherMethod') : z.verfahren || t('siteDocs.assembly.otherMethod')}</option>
                  {MONTAGE_VERFAHREN.map((v) => (
                    <option key={v} value={v}>
                      {MONTAGE_VERFAHREN_TEXT[v]}
                    </option>
                  ))}
                </select>
                <TextFeld inputMode="decimal" wert={z.sollMoment} platzhalter="Nm" onSpeichern={(w) => zeile({ sollMoment: w })} />
                <TextFeld wert={z.pruefmittel} platzhalter={t('siteDocs.assembly.equipment')} onSpeichern={(w) => zeile({ pruefmittel: w })} />
                <input type="date" className={feldKlasse} aria-label={t('siteDocs.assembly.calibratedUntil')} value={z.kalibriertBis ?? ''} onChange={(e) => zeile({ kalibriertBis: e.target.value || null })} />
                <TextFeld inputMode="numeric" wert={z.geprueft == null ? null : String(z.geprueft)} platzhalter={t('siteDocs.assembly.checked')} onSpeichern={(w) => zeile({ geprueft: w ? Math.max(0, Math.trunc(Number(w)) || 0) : null })} />
                <select className={feldKlasse} aria-label={t('siteDocs.assembly.result')} value={z.ergebnis ?? ''} onChange={(e) => zeile({ ergebnis: (e.target.value || null) as 'OK' | 'NOK' | null })}>
                  <option value="">—</option>
                  <option value="OK">{t('siteDocs.assembly.results.OK')}</option>
                  <option value="NOK">{t('siteDocs.assembly.results.NOK')}</option>
                </select>
                <button type="button" className={cn(knopf, 'text-red-700')} onClick={() => onChange({ ...b, anziehprotokoll: b.anziehprotokoll.filter((x) => x.key !== z.key) })} title={t('siteDocs.assembly.removeRow')}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                {(z.ergebnis === 'NOK' || z.bemerkung) && <TextFeld className="sm:col-span-10" wert={z.bemerkung} platzhalter={t('siteDocs.assembly.remark')} onSpeichern={(w) => zeile({ bemerkung: w })} />}
              </div>
            )
          })}
        </div>
      )}
      {!nurAbnahme && b.detailgrad === 'NACHWEIS' && b.tabellen.vermessung !== 'AUS' && (
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase text-muted-foreground">
              {t('siteDocs.assembly.survey')}
              {b.tabellen.vermessung === 'PFLICHT' && ` (${t('siteDocs.assembly.required')})`}
            </h4>
            <button type="button" className={knopf} onClick={() => onChange({ ...b, vermessung: [...b.vermessung, { key: schluessel(), punkt: '', richtung: 'X', soll: null, ist: null, einheit: 'mm', toleranz: null, ergebnis: null, ueberschrieben: false, bemerkung: null }] })}>
              <Plus className="h-3.5 w-3.5" /> {t('siteDocs.assembly.addRow')}
            </button>
          </div>
          {b.vermessung.map((z) => {
            const zeile = (teil: Partial<typeof z>) => onChange({ ...b, vermessung: b.vermessung.map((x) => (x.key === z.key ? { ...x, ...teil } : x)) })
            const erg = montageVermessGeltend(z)
            const abw = montageAbweichung(z)
            return (
              <div key={z.key} className="grid items-center gap-1 sm:grid-cols-[2fr_7rem_5rem_5rem_5rem_5rem_auto_auto_auto]">
                <TextFeld wert={z.punkt} platzhalter={t('siteDocs.assembly.surveyPoint')} onSpeichern={(w) => zeile({ punkt: w ?? '' })} />
                <select className={feldKlasse} aria-label={t('siteDocs.assembly.direction')} value={z.richtung} onChange={(e) => zeile({ richtung: e.target.value as typeof z.richtung })}>
                  {MONTAGE_RICHTUNGEN.map((r) => (
                    <option key={r} value={r}>
                      {t(`siteDocs.assembly.directions.${r}`)}
                    </option>
                  ))}
                </select>
                <TextFeld inputMode="decimal" wert={z.soll} platzhalter={t('siteDocs.assembly.target')} onSpeichern={(w) => zeile({ soll: w })} />
                <TextFeld inputMode="decimal" wert={z.ist} platzhalter={t('siteDocs.assembly.actual')} onSpeichern={(w) => zeile({ ist: w })} />
                <TextFeld inputMode="decimal" wert={z.toleranz} platzhalter="±" onSpeichern={(w) => zeile({ toleranz: w })} />
                <TextFeld wert={z.einheit} platzhalter={t('siteDocs.assembly.unit')} onSpeichern={(w) => zeile({ einheit: w ?? 'mm' })} />
                <span className={cn('text-xs', erg === 'NOK' && 'font-semibold text-red-700')}>{abw ? `${abw.replace('.', ',')} → ${erg ? t(`siteDocs.assembly.results.${erg}`) : ''}` : ''}</span>
                <label className="flex items-center gap-1 text-xs">
                  <input type="checkbox" checked={z.ueberschrieben} onChange={(e) => zeile(e.target.checked ? { ueberschrieben: true, ergebnis: erg === 'OK' ? 'NOK' : 'OK' } : { ueberschrieben: false, ergebnis: null })} />
                  {t('siteDocs.assembly.override')}
                </label>
                <button type="button" className={cn(knopf, 'text-red-700')} onClick={() => onChange({ ...b, vermessung: b.vermessung.filter((x) => x.key !== z.key) })} title={t('siteDocs.assembly.removeRow')}>
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
                {(z.ueberschrieben || erg === 'NOK' || z.bemerkung) && <TextFeld className="sm:col-span-9" wert={z.bemerkung} platzhalter={t('siteDocs.assembly.remark')} onSpeichern={(w) => zeile({ bemerkung: w })} />}
              </div>
            )
          })}
        </div>
      )}
      <Rueckfrage
        offen={!!wechsel}
        titel={t('siteDocs.assembly.changeLevel')}
        onAbbrechen={() => setWechsel(null)}
        abbrechenLabel={t('common.cancel')}
        aktionen={[{ label: t('siteDocs.assembly.change'), art: 'gefahr', onClick: () => { if (wechsel) onChange(montageDetailgradWechseln(b, wechsel)); setWechsel(null) } }]}
      >
        <p>{t('siteDocs.assembly.changeLevelText')}</p>
      </Rueckfrage>
    </div>
  )
}

function MaengelEditor({ inhalt, aendere }: { inhalt: MontageInhalt; aendere: (f: (i: MontageInhalt) => MontageInhalt) => void }) {
  const { t } = useTranslation()
  const setze = (nr: number, teil: Partial<MontageMangel>) => aendere((i) => ({ ...i, maengel: i.maengel.map((m) => (m.nr === nr ? { ...m, ...teil } : m)) }))
  return (
    <section className="space-y-1">
      <div className="flex items-center justify-between">
        <h3 className="font-medium">{t('siteDocs.assembly.defects')}</h3>
        <button type="button" className={knopf} onClick={() => aendere((i) => ({ ...i, maengel: [...i.maengel, { nr: montageNaechsteNr(i.maengel), bauteilKey: null, text: '', gewicht: null, frist: null, fotos: [] }] }))}>
          <Plus className="h-3.5 w-3.5" /> {t('siteDocs.assembly.addDefect')}
        </button>
      </div>
      {[...inhalt.maengel]
        .sort((a, b) => a.nr - b.nr)
        .map((m) => (
          <div key={m.nr} className="grid items-start gap-1 sm:grid-cols-[2rem_10rem_1fr_9rem_9rem_auto]">
            <span className="py-1.5 font-medium">{m.nr}.</span>
            <select className={feldKlasse} aria-label={t('siteDocs.assembly.part')} value={m.bauteilKey ?? ''} onChange={(e) => setze(m.nr, { bauteilKey: e.target.value || null })}>
              <option value="">{t('siteDocs.assembly.general')}</option>
              {inhalt.bauteile.map((b) => (
                <option key={b.key} value={b.key}>
                  {b.bezeichnung || b.key}
                </option>
              ))}
            </select>
            <TextFeld mehrzeilig wert={m.text} platzhalter={t('siteDocs.assembly.defectText')} onSpeichern={(w) => setze(m.nr, { text: w ?? '' })} />
            <select className={cn(feldKlasse, !m.gewicht && 'border-amber-500')} aria-label={t('siteDocs.assembly.weight')} value={m.gewicht ?? ''} onChange={(e) => setze(m.nr, { gewicht: (e.target.value || null) as MontageMangel['gewicht'] })}>
              <option value="">{t('siteDocs.assembly.weightChoose')}</option>
              <option value="WESENTLICH">{t('siteDocs.assembly.weights.WESENTLICH')}</option>
              <option value="UNWESENTLICH">{t('siteDocs.assembly.weights.UNWESENTLICH')}</option>
            </select>
            <input type="date" className={feldKlasse} aria-label={t('siteDocs.assembly.due')} value={m.frist ?? ''} onChange={(e) => setze(m.nr, { frist: e.target.value || null })} />
            <button type="button" className={cn(knopf, 'text-red-700')} onClick={() => aendere((i) => ({ ...i, maengel: i.maengel.filter((x) => x.nr !== m.nr) }))} title={t('siteDocs.assembly.removeDefect')}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
    </section>
  )
}

function AbnahmeEditor({ inhalt, aendere }: { inhalt: MontageInhalt; aendere: (f: (i: MontageInhalt) => MontageInhalt) => void }) {
  const { t } = useTranslation()
  const a = inhalt.abnahme!
  const teil = (x: Partial<NonNullable<MontageInhalt['abnahme']>>) => aendere((i) => ({ ...i, abnahme: { ...(i.abnahme ?? montageAbnahmeLeer()), ...x } }))
  return (
    <section className="space-y-2 rounded-md border-2 border-primary/40 p-2">
      <h3 className="font-medium">{t('siteDocs.assembly.acceptance')}</h3>
      <div className="flex flex-wrap gap-2">
        {MONTAGE_ABNAHME_ERGEBNISSE.map((e) => (
          <button key={e} type="button" className={cn(knopf, a.ergebnis === e && 'border-primary bg-primary/10')} onClick={() => teil({ ergebnis: e })}>
            {t(`siteDocs.assembly.acceptanceResults.${e}`)}
          </button>
        ))}
      </div>
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={a.teilabnahme} onChange={(e) => teil({ teilabnahme: e.target.checked })} /> {t('siteDocs.assembly.partial')}
      </label>
      <TextFeld mehrzeilig wert={a.vorbehalt} platzhalter={a.ergebnis === 'NICHT_ABGENOMMEN' ? t('siteDocs.assembly.reason') : t('siteDocs.assembly.reservation')} onSpeichern={(w) => teil({ vorbehalt: w })} />
      {a.bezug.length > 0 && (
        <p>
          {t('siteDocs.assembly.refersTo')}: {a.bezug.map((b) => b.nummer).join(', ')}
        </p>
      )}
      {a.behoben.map((b) => (
        <p key={`${b.nummer}-${b.nr}`} className="flex items-center gap-2">
          {t('siteDocs.assembly.fixedLine', { nummer: b.nummer, nr: b.nr, text: b.text })}
          <button type="button" className="text-xs text-red-700" onClick={() => teil({ behoben: a.behoben.filter((x) => !(x.nummer === b.nummer && x.nr === b.nr)) })}>
            {t('siteDocs.assembly.remove')}
          </button>
        </p>
      ))}
    </section>
  )
}

function KandidatenDialog({ dok, onClose, aendere }: { dok: SiteDocDetail; onClose: () => void; aendere: (f: (i: MontageInhalt) => MontageInhalt) => void }) {
  const { t } = useTranslation()
  const [liste, setListe] = useState<MontageAbnahmeKandidat[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [gewaehlt, setGewaehlt] = useState<Set<string>>(new Set())
  const [behoben, setBehoben] = useState<Set<string>>(new Set())
  useEffect(() => {
    fetchAssemblyCandidates({ projectId: dok.projectId, projectRefId: dok.projectRefId, projectLabel: dok.projectId || dok.projectRefId ? null : dok.projectLabel })
      .then(setListe)
      .catch((e) => setFehler(fehlertext(e, t)))
  }, [dok.projectId, dok.projectRefId, dok.projectLabel, t])
  const umschalten = (s: Set<string>, id: string, an: boolean) => {
    const n = new Set(s)
    if (an) n.add(id)
    else n.delete(id)
    return n
  }
  const uebernehmen = () => {
    const l = liste ?? []
    aendere((i) => {
      const a = i.abnahme ?? montageAbnahmeLeer()
      const bezug = [...a.bezug]
      const neueBehoben = [...a.behoben]
      let bauteile = [...i.bauteile]
      for (const k of l) {
        if (gewaehlt.has(k.documentId) && !bezug.some((b) => b.nummer === k.number)) {
          bezug.push({ nummer: k.number, pruefwert: k.contentHash })
          for (const b of k.bauteile) {
            const key = `${k.number}-${b.key}`.slice(0, 64)
            if (!bauteile.some((x) => x.key === key)) bauteile = [...bauteile, montageBauteilNeu(key, naechste(bauteile), b.bezeichnung, b.exc, 'EINFACH')]
          }
        }
        for (const m of k.offeneMaengel) if (behoben.has(`${k.number}#${m.nr}`) && !neueBehoben.some((x) => x.nummer === k.number && x.nr === m.nr)) neueBehoben.push({ nummer: k.number, nr: m.nr, text: m.text })
      }
      return { ...i, bauteile, abnahme: { ...a, bezug, behoben: neueBehoben } }
    })
    onClose()
  }
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('siteDocs.assembly.fromRecords')}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <button type="button" className="h-10 rounded-md border px-4 text-sm" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="button" className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground" disabled={!liste?.length} onClick={uebernehmen}>
            {t('siteDocs.assembly.apply')}
          </button>
        </div>
      }
    >
      <div className="space-y-2 text-sm">
        {fehler && <p className="text-destructive">{fehler}</p>}
        {liste && !liste.length && <p className="text-muted-foreground">{t('siteDocs.assembly.noCandidates')}</p>}
        {(liste ?? []).map((k) => (
          <div key={k.documentId} className="space-y-1 rounded-md border p-2">
            <label className="flex items-center gap-2 font-medium">
              <input type="checkbox" checked={gewaehlt.has(k.documentId)} onChange={(e) => setGewaehlt((s) => umschalten(s, k.documentId, e.target.checked))} />
              {k.number} · {datumDe(k.documentDate)} · {t(`siteDocs.assembly.scopes.${k.umfang}`)}
            </label>
            <p className="text-xs text-muted-foreground">{k.bauteile.map((b) => b.bezeichnung).join(', ')}</p>
            {k.offeneMaengel.map((m) => (
              <label key={m.nr} className="flex items-center gap-2">
                <input type="checkbox" checked={behoben.has(`${k.number}#${m.nr}`)} onChange={(e) => setBehoben((s) => umschalten(s, `${k.number}#${m.nr}`, e.target.checked))} />
                {t('siteDocs.assembly.tickFixed', { nr: m.nr, text: m.text })}
              </label>
            ))}
          </div>
        ))}
      </div>
    </Modal>
  )
}

/** AP-47 13. im ERP — Mängel aus dem Diktat; Gewicht und Frist bleiben leer (MO-15). */
function MontageErpDiktat({ dok, inhalt, aendere }: { dok: SiteDocDetail; inhalt: MontageInhalt; aendere: (f: (i: MontageInhalt) => MontageInhalt) => void }) {
  const { t } = useTranslation()
  const [text, setText] = useState(dok.dictation ?? '')
  const [vorschlag, setVorschlag] = useState<SiteDocAiProposal | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  type Eintrag = SiteDocAiProposalItem<MontageDiktatMangel | { kopf: string }>
  const eintraege = useMemo<Eintrag[]>(() => {
    if (!vorschlag) return []
    const maengel = (vorschlag.items as unknown as SiteDocAiProposalItem<MontageDiktatMangel>[]).map((e) => ({ ...e, daten: { ...e.daten, bauteilKey: montageBauteilZuordnen(e.daten.bauteilGenannt, inhalt.bauteile) } }))
    const kopf = typeof vorschlag.kopf?.bemerkung === 'string' ? vorschlag.kopf.bemerkung : null
    return kopf ? [{ key: 'kopf', daten: { kopf }, vorgewaehlt: !inhalt.bemerkung, hinweise: [] }, ...maengel] : maengel
  }, [vorschlag, inhalt.bauteile, inhalt.bemerkung])
  const namen = new Map(inhalt.bauteile.map((b) => [b.key, b.bezeichnung]))
  return (
    <div className="space-y-2 rounded-md border p-2">
      <DiktatFeld
        wert={text}
        onChange={setText}
        onBlur={() => text !== (dok.dictation ?? '') && void updateSiteDoc('ASSEMBLY', dok.id, { dictation: text || null }).catch(() => undefined)}
        onVorschlag={async () => {
          setFehler(null)
          try {
            setVorschlag(await siteDocAiProposal({ kind: 'ASSEMBLY', text, projectId: dok.projectId, projectRefId: dok.projectRefId, documentDate: dok.documentDate.slice(0, 10) }))
          } catch (e) {
            setFehler(fehlertext(e, t))
          }
        }}
        online={navigator.onLine}
        fehler={fehler}
        texte={{ titel: t('siteDocs.assembly.dictationTitle'), tastatur: t('siteDocs.diktat.tastatur'), knopf: t('siteDocs.diktat.knopf'), arbeitet: t('siteDocs.diktat.arbeitet'), ohneNetz: t('siteDocs.diktat.ohneNetz'), kiHinweis: t('siteDocs.diktat.kiHinweis') }}
      />
      {vorschlag && (
        <VorschlagsKasten
          eintraege={eintraege}
          darstellung={(e: Eintrag) => ('kopf' in e.daten ? <span>{t('siteDocs.assembly.dictationRemark', { text: e.daten.kopf })}</span> : <span>{e.daten.text} <span className="text-muted-foreground">({e.daten.bauteilKey ? namen.get(e.daten.bauteilKey) : t('siteDocs.assembly.general')})</span></span>)}
          onUebernehmen={(liste: Eintrag[]) => {
            aendere((i) => {
              let bemerkung = i.bemerkung
              const maengel = [...i.maengel]
              for (const e of liste) {
                if ('kopf' in e.daten) bemerkung = bemerkung ? `${bemerkung}\n${e.daten.kopf}` : e.daten.kopf
                else maengel.push(montageMangelAusDiktat(e.daten, montageNaechsteNr(maengel)))
              }
              return { ...i, bemerkung, maengel }
            })
            setVorschlag(null)
          }}
          onVerwerfen={() => setVorschlag(null)}
          texte={{ titel: t('siteDocs.diktat.vorschlag'), uebernehmen: t('siteDocs.diktat.uebernehmen'), markierte: t('siteDocs.diktat.markierte'), verwerfen: t('siteDocs.diktat.verwerfen'), leer: t('siteDocs.diktat.leer'), hinweis: (c) => t(`siteDocs.diktat.hints.${c}`, { defaultValue: c }) }}
        />
      )}
    </div>
  )
}

// ── Eingefroren: Zuordnung und Bürostand ────────────────────────────────────

function MontageBuero({ dok, onGeaendert }: ErpDetailProps) {
  const { t } = useTranslation()
  const { user, hasModule } = useAuthStore()
  const darfVerwalten = erfuelltRecht('sitedocs.assembly.manage', user?.permissions)
  const inhalt = montageInhaltVon(dok.content)
  const [positionen, setPositionen] = useState<MontagePosition[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [vermerk, setVermerk] = useState<{ id: string; art: 'resolve' | 'reopen' } | null>(null)
  const [notiz, setNotiz] = useState('')
  const zuordnenMoeglich = darfVerwalten && !!dok.projectId && hasModule('PROJECT_MGMT.CORE') && dok.status !== 'VOIDED' && dok.status !== 'DISCARDED'

  useEffect(() => {
    if (!zuordnenMoeglich || !dok.projectId) return
    fetchAssemblyProjectParts(dok.projectId)
      .then(setPositionen)
      .catch(() => setPositionen([]))
  }, [zuordnenMoeglich, dok.projectId])

  const zuordnen = async (partKey: string, positionId: string | null, workStepId: string | null) => {
    setFehler(null)
    try {
      onGeaendert(await linkAssemblyPart(dok.id, partKey, { positionId, workStepId }))
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  const vermerken = async () => {
    if (!vermerk) return
    setFehler(null)
    try {
      if (vermerk.art === 'resolve') await resolveAssemblyDefect(vermerk.id, notiz)
      else await reopenAssemblyDefect(vermerk.id, notiz || undefined)
      setVermerk(null)
      setNotiz('')
      onGeaendert()
    } catch (e) {
      setFehler(fehlertext(e, t))
    }
  }

  const sichtbar = dok.status === 'FINAL' || dok.status === 'DECLINED' || dok.status === 'AWAITING'
  return (
    <div className="space-y-3 text-sm">
      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-destructive">{fehler}</p>}
      {zuordnenMoeglich && inhalt.bauteile.length > 0 && (
        <section className="space-y-1">
          <h3 className="font-medium">{t('siteDocs.assembly.assignParts')}</h3>
          {montageSortiert(inhalt.bauteile).map((b) => {
            const pos = positionen.find((p) => p.id === b.positionId)
            return (
              <div key={b.key} className="grid items-center gap-2 sm:grid-cols-[1fr_14rem_14rem]">
                <span>{b.bezeichnung}</span>
                <select className={feldKlasse} aria-label={t('siteDocs.assembly.position')} value={b.positionId ?? ''} onChange={(e) => void zuordnen(b.key, e.target.value || null, null)}>
                  <option value="">{t('siteDocs.assembly.noPosition')}</option>
                  {positionen.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.number} {p.name}
                    </option>
                  ))}
                </select>
                <select className={feldKlasse} aria-label={t('siteDocs.assembly.workStep')} disabled={!pos} value={b.workStepId ?? ''} onChange={(e) => void zuordnen(b.key, b.positionId ?? null, e.target.value || null)}>
                  <option value="">{t('siteDocs.assembly.noWorkStep')}</option>
                  {(pos?.steps ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.number} {s.name}
                      {s.isAssembly ? ` (${t('siteDocs.assembly.assemblyStep')})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )
          })}
        </section>
      )}
      {sichtbar && inhalt.maengel.length > 0 && (
        <section className="space-y-1">
          <h3 className="font-medium">{t('siteDocs.assembly.defectState')}</h3>
          <ul className="divide-y rounded-md border">
            {inhalt.maengel.map((m) => (
              <li key={m.nr} className="flex flex-wrap items-center gap-2 px-2 py-1">
                <span className="min-w-0 flex-1">
                  <strong>{m.nr}.</strong> {m.text}
                </span>
                <span className={cn('rounded-full px-2 py-0.5 text-xs', m.state === 'OPEN' ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100' : 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100')}>
                  {m.state ? MONTAGE_ZUSTAND_TEXT[m.state] : ''}
                  {m.resolvedByNumber ? ` (${m.resolvedByNumber})` : ''}
                </span>
                {m.resolvedNote && m.state === 'RESOLVED_OFFICE' && <span className="text-xs text-muted-foreground">{m.resolvedNote}</span>}
                {m.state === 'OPEN' && m.taskDoneAt && <span className="text-xs text-muted-foreground">{t('siteDocs.assembly.taskDone')}</span>}
                {m.projectTaskId && hasModule('PROJECT_MGMT.CORE') && (
                  <Link to="/project-tasks" className="text-xs text-primary hover:underline">
                    {t('siteDocs.assembly.openTask')}
                  </Link>
                )}
                {darfVerwalten && m.id && m.state === 'OPEN' && dok.status !== 'AWAITING' && (
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => setVermerk({ id: m.id!, art: 'resolve' })}>
                    {t('siteDocs.assembly.markResolved')}
                  </button>
                )}
                {darfVerwalten && m.id && m.state === 'RESOLVED_OFFICE' && (
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => setVermerk({ id: m.id!, art: 'reopen' })}>
                    {t('siteDocs.assembly.reopen')}
                  </button>
                )}
              </li>
            ))}
          </ul>
          <Link to={`/site-docs/defects?documentId=${dok.id}`} className="text-xs text-primary hover:underline">
            {t('siteDocs.assembly.toDefectList')}
          </Link>
        </section>
      )}
      {vermerk && (
        <Modal
          isOpen
          onClose={() => setVermerk(null)}
          title={vermerk.art === 'resolve' ? t('siteDocs.assembly.markResolved') : t('siteDocs.assembly.reopen')}
          size="sm"
          footer={
            <div className="flex w-full justify-end gap-2">
              <button type="button" className="h-10 rounded-md border px-4 text-sm" onClick={() => setVermerk(null)}>
                {t('common.cancel')}
              </button>
              <button type="button" className="h-10 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground" disabled={vermerk.art === 'resolve' && !notiz.trim()} onClick={() => void vermerken()}>
                {vermerk.art === 'resolve' ? t('siteDocs.assembly.markResolved') : t('siteDocs.assembly.reopen')}
              </button>
            </div>
          }
        >
          <p className="mb-2 text-sm text-muted-foreground">{vermerk.art === 'resolve' ? t('siteDocs.assembly.resolveHint') : t('siteDocs.assembly.reopenHint')}</p>
          <textarea className={`${feldKlasse} min-h-20`} value={notiz} onChange={(e) => setNotiz(e.target.value)} placeholder={t('siteDocs.assembly.note')} aria-label={t('siteDocs.assembly.note')} />
        </Modal>
      )}
    </div>
  )
}
